import { matchPath } from 'react-router-dom';

import { StringUtils } from '@spotify/confidence-core-react';
import type { ObservationRefFragment } from '@spotify/plugin-confidence-issues-react';

import type { EvidenceBlock, EvidenceFinding } from './parseEvidenceBlock';

export type EvidenceSourceKind =
  | 'recording'
  | 'change'
  | 'release'
  | 'errors'
  | 'reports'
  | 'observation';

export type EvidenceItemState =
  | 'onIssue'
  | 'notLinked'
  | 'elsewhere'
  | 'missing'
  | 'notLoaded'
  | 'unknown';

export type EvidenceItem = {
  name: string;
  observation?: ObservationRefFragment;
  state: EvidenceItemState;
};

export type EvidenceFindingView = {
  id: string;
  finding: EvidenceFinding;
  items: EvidenceItem[];
  timestamp?: number;
  kind: EvidenceSourceKind;
  sourceParts: string[];
  countLabel: string;
  hasMissingEvidence: boolean;
};

type SourceDescription = {
  kind: EvidenceSourceKind;
  label: string;
  noun: string;
};

const SOURCES: Record<string, SourceDescription> = {
  recordings: {
    kind: 'recording',
    label: 'Session recordings',
    noun: 'recording',
  },
  github: { kind: 'change', label: 'GitHub', noun: 'commit' },
  deployment: { kind: 'release', label: 'Deployment', noun: 'release' },
  sentry: { kind: 'errors', label: 'Sentry', noun: 'error' },
  zendesk: { kind: 'reports', label: 'Zendesk', noun: 'report' },
  intercom: { kind: 'reports', label: 'Intercom', noun: 'report' },
  slack: { kind: 'reports', label: 'Slack', noun: 'message' },
};

const OBSERVATION_NOUN = 'observation';
const MAX_EXTERNAL_ID_LENGTH = 40;

export const formatCount = (count: number, noun: string): string =>
  `${count} ${StringUtils.pluralize(count, noun)}`;

export const isMissingEvidence = (state: EvidenceItemState): boolean =>
  state === 'missing' || state === 'elsewhere';

const describeSource = (
  observation: ObservationRefFragment,
): SourceDescription =>
  SOURCES[observation.source.system.trim().toLowerCase()] ?? {
    kind: 'observation',
    label: observation.source.system,
    noun: OBSERVATION_NOUN,
  };

export const getRecordingReference = (
  observation: ObservationRefFragment,
): { name: string; findingId?: string } | undefined => {
  const references = [observation.source.externalId, observation.source.uri];
  for (const reference of references) {
    if (!reference) continue;
    const match = matchPath(
      'recordings/:id/findings/:findingId',
      `/${reference}`,
    );
    if (match?.params.id) {
      return {
        name: `recordings/${match.params.id}`,
        findingId: match.params.findingId,
      };
    }
  }
  for (const reference of references) {
    if (!reference) continue;
    const match = matchPath('recordings/:id', `/${reference}`);
    if (match?.params.id) return { name: `recordings/${match.params.id}` };
  }
  return undefined;
};

const getRecordingName = (
  observation: ObservationRefFragment,
): string | undefined => getRecordingReference(observation)?.name;

const getItemState = (
  observation: ObservationRefFragment | undefined,
  issueName: string | undefined,
): EvidenceItemState => {
  if (!observation) return 'missing';
  if (!issueName) return 'onIssue';
  const { issue } = observation;
  if (!issue) return 'notLinked';
  if (!('name' in issue)) return 'unknown';
  return issue.name === issueName ? 'onIssue' : 'elsewhere';
};

export const getSourceKind = (
  observation: ObservationRefFragment,
): EvidenceSourceKind => describeSource(observation).kind;

export const getTimestamp = (
  observation: ObservationRefFragment,
): number | undefined => {
  const timestamp = Date.parse(observation.eventTime);
  return Number.isFinite(timestamp) ? timestamp : undefined;
};

export const buildFindingViews = (
  block: EvidenceBlock,
  observationsByName: ReadonlyMap<string, ObservationRefFragment>,
  issueName: string | undefined,
  requestedNames?: ReadonlySet<string>,
): EvidenceFindingView[] =>
  block.findings.map((finding, index) => {
    const items = finding.observations.map(name => {
      const observation = observationsByName.get(name);
      const state =
        !observation && requestedNames && !requestedNames.has(name)
          ? 'notLoaded'
          : getItemState(observation, issueName);
      return { name, observation, state };
    });
    const found = items
      .flatMap(item => (item.observation ? [item.observation] : []))
      .sort(
        (left, right) =>
          (getTimestamp(left) ?? Infinity) - (getTimestamp(right) ?? Infinity),
      );
    const first = found[0];
    const sources = found.map(describeSource);
    const source = sources[0];
    const sharedNoun =
      source && sources.every(other => other.label === source.label)
        ? source.noun
        : OBSERVATION_NOUN;
    const externalId =
      found.length === 1 &&
      first?.source.externalId &&
      first.source.externalId.length <= MAX_EXTERNAL_ID_LENGTH &&
      !getRecordingName(first)
        ? first.source.externalId
        : undefined;

    return {
      id: `finding-${index}`,
      finding,
      items,
      timestamp: first ? getTimestamp(first) : undefined,
      kind: source?.kind ?? 'observation',
      sourceParts: [source?.label, externalId, finding.summary].filter(
        (part): part is string => Boolean(part),
      ),
      countLabel: formatCount(items.length, sharedNoun),
      hasMissingEvidence: items.some(item => isMissingEvidence(item.state)),
    };
  });

export type EvidenceDateRange = { first: number; last: number };

export const buildEvidenceDateRange = (
  views: readonly EvidenceFindingView[],
): EvidenceDateRange | undefined => {
  const timestamps = views.flatMap(view =>
    view.items.flatMap(({ observation }) => {
      const timestamp = observation && getTimestamp(observation);
      return timestamp === undefined ? [] : [timestamp];
    }),
  );
  if (timestamps.length === 0) return undefined;
  return { first: Math.min(...timestamps), last: Math.max(...timestamps) };
};
